import { useEffect, useState } from 'react'

import { DEFAULT_BANNER_FONTS, IRichTextFont, setBannerFonts } from '../../../components/RichTextField'
import { useAppToast } from '../../../helpers'
import { IWebsiteFont } from '../../../interfaces/Website/Banners/IWebsiteFont'
import WebsiteFontService from '../../../services/Website/WebsiteFontService'
import { bannerFontCss, isSafeFontSlug } from './bannerFontCss'

const STYLE_ELEMENT_ID = 'banner-fonts'

/** As três do site mais as enviadas, no formato do seletor do editor. */
const toRichTextFonts = (uploaded: IWebsiteFont[]): IRichTextFont[] => [
  ...DEFAULT_BANNER_FONTS,
  ...uploaded.map(({ slug, name }) => ({ slug, name }))
]

/** Escreve (ou reescreve) o `<style>` das fontes enviadas no `<head>`. Um só para a página inteira. */
export function applyBannerFontCss(fonts: IWebsiteFont[]) {
  let style = document.getElementById(STYLE_ELEMENT_ID)

  if (!style) {
    style = document.createElement('style')
    style.id = STYLE_ELEMENT_ID
    document.head.appendChild(style)
  }

  style.textContent = bannerFontCss(fonts)
}

/**
 * As fontes do editor dos Banners Home: as três do site mais as enviadas pela aba "Fontes".
 *
 * `isLoaded` existe porque o editor precisa da lista **antes** de montar (ver `setBannerFonts`).
 * Se a busca falhar, o editor segue com as três do site — um banner com fonte enviada perde a
 * fonte na tela, mas só é regravado sem ela se alguém editar aquele trecho.
 */
export function useBannerFonts() {
  const toast = useAppToast()
  const [uploaded, setUploaded] = useState<IWebsiteFont[]>([])
  const [isLoaded, setIsLoaded] = useState(false)
  const [loadError, setLoadError] = useState(false)

  const load = async () => {
    setLoadError(false)

    try {
      const fonts = (await new WebsiteFontService().list()).filter(({ slug }) => isSafeFontSlug(slug))
      applyBannerFontCss(fonts)
      // Aqui, e não num `useEffect`: os editores montam no mesmo commit em que `isLoaded` vira
      // `true`, e os efeitos dos filhos rodam antes dos do pai — num efeito, o Quill já teria
      // lido o HTML com a whitelist antiga e descartado as fontes enviadas.
      setBannerFonts(toRichTextFonts(fonts))
      setUploaded(fonts)
    } catch (error) {
      setLoadError(true)
      toast.error('Erro ao carregar as fontes dos banners', error)
    } finally {
      setIsLoaded(true)
    }
  }

  useEffect(() => {
    load()
  }, [])

  return { fonts: toRichTextFonts(uploaded), uploaded, isLoaded, loadError, reload: load }
}
